import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { DeleteJson, GetJson, PostJson, PutJson } from '@awp/helpers/fetchReact';
import { FORM_MODE, Permissions, REST_API, Views } from '@awp/constants';
import { Card, Col, Container, Row } from 'react-bootstrap';
import { FormDatePicker, FormInput, FormSelect, FormTextReadOnly } from '@awp/components/Common/AwpFormInputs';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { isJsonValueEmpty, isValueEmpty } from '@awp/helpers/utilsReact';
import ModelMetaBoxEdit from '@awp/components/MetaFields/ModelMetaBoxEdit';
import ModelMetaBoxInfo from '@awp/components/MetaFields/ModelMetaBoxInfo';
import AwpValidationErrorModal from '@awp/components/Common/AwpValidationErrorModal';
import AwpYesNoDialog from '@awp/components/Common/AwpYesNoDialog';
import AwpFormActionButtons from '@awp/components/Common/AwpFormActionButtons';
import AwpSpinner from '@awp/components/Common/AwpSpinner';
import { selectAllSensormodels, selectAllSensorstatuses } from '@stores/sensorEquipmentSlice';
import isEqual from 'lodash/isEqual';
import SensorEquipmentService from '@components/SensorAdmin/SensorEquipmentService';

const SensorEdit = ({ setMode, mode, sensorId, data, setData, setLoading, loading }) => {
  const allAllSensorstatuses = useSelector(selectAllSensorstatuses);
  const allAllSensormodels = useSelector(selectAllSensormodels);

  const [sensorData, setSensorData] = useState({});
  const [entityErrors, setEntityErrors] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [initialDto, setInitialDto] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});

  const crudService = new SensorEquipmentService(mode);
  const { readOnly, title, buttonLabel, successMessage, entityName } = crudService.crud;

  useEffect(() => {
    if (mode === FORM_MODE.CREATE) {
      setSensorData({ Name: '', Note: '' });
      setInitialDto(crudService.mapInitialValues({ entity: sensorData, getDefaultSensorStatus }));
      return;
    }

    setLoading(true);
    const url = REST_API.SENSOR_EQUIPMENT + sensorId;

    GetJson(url)
      .then((response) => response.json())
      .then((snsr) => {
        setSensorData({ ...snsr, ModelId: parseInt(snsr.ModelId) });
        setInitialDto(crudService.mapInitialValues({ entity: snsr, getDefaultSensorStatus }));
        setLoading(false);
      });
  }, []);
  const convertDropdownOptions = (options) => {
    if (isValueEmpty(options) || options.length === 0) return [];
    let newOptions = [];
    options.map((t) => {
      newOptions.push({ value: t.Id, label: t.DropdownText });
    });
    return newOptions;
  };
  const updateSensor = (setSubmitting, resetForm, setErrors, sensorDto) => {
    const url = REST_API.SENSOR_EQUIPMENT + sensorDto.Id;

    PutJson(url, sensorDto)
      .then(async (response) => {
        if (response.ok) {
          toast.success(successMessage);
          const snsr = await response.json();
          setSensorData(snsr);
          updateData(snsr);

          const updatedDto = crudService.mapInitialValues({ entity: snsr, getDefaultSensorStatus });
          resetForm({
            values: updatedDto,
          });
          setInitialDto(updatedDto);
        } else if (response.status === 412) {
          const errors = await response.json();
          setErrors(errors);
          if (errors.entity) setEntityErrors({ Sensor: errors.entity });
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const createSensor = (setSubmitting, resetForm, setErrors, sensorDto) => {
    const url = REST_API.SENSOR_EQUIPMENT;

    PostJson(url, sensorDto)
      .then(async (response) => {
        if (response.ok) {
          const sensor = await response.json();
          setSensorData(sensor);
          updateData(sensor);
          toast.success(successMessage);
          setMode(FORM_MODE.VIEW);

          const updatedDto = crudService.mapInitialValues({ entity: sensor, getDefaultSensorStatus });
          resetForm({
            values: updatedDto,
          });
          setInitialDto(updatedDto);
        } else if (response.status === 412) {
          const errors = await response.json();
          setErrors(errors);
          if (errors.entity) setEntityErrors({ Sensor: errors.entity });
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };
  const deleteSensor = () => {
    const id = sensorData.Id;
    const url = REST_API.SENSOR_EQUIPMENT + id;

    DeleteJson(url, {})
      .then(async (response) => {
        if (response.ok) {
          setSensorData({});
          removeData(id);
          toast.success(`${entityName} has been deleted`);
          setMode(FORM_MODE.LIST);
        } else if (response.status === 412) {
          const errors = await response.json();
          //setErrors(errors);
          if (errors.entity) setEntityErrors({ Sensor: errors.entity });
        }
      })
      .finally(() => {
        //setSubmitting(false);
      });
  };
  const handleSubmit = (values, { setSubmitting, resetForm, setErrors }) => {
    setSubmitting(true);

    const entityDto = crudService.mapFormToDTO({ entity: values, sensorData });

    if (mode === FORM_MODE.EDIT) {
      updateSensor(setSubmitting, resetForm, setErrors, entityDto);
    } else {
      createSensor(setSubmitting, resetForm, setErrors, entityDto);
    }
  };

  const updateData = (snsr) => {
    if (mode === FORM_MODE.EDIT) {
      const index = data.findIndex((x) => x.Id === snsr.Id);
      if (index < 0) return;
      data[index] = snsr;
      setData([...data]);
    } else {
      setData([...data, snsr]);
    }
  };
  const removeData = (Id) => {
    if (mode === FORM_MODE.EDIT) {
      let _data = [...data];
      let _removedData = [];
      _data.map((o) => {
        if (o.Id !== Id) {
          _removedData.push(o);
        }
      });
      setData([..._removedData]);
    }
  };
  const onClickDelete = () => {
    setShowDeleteConfirm(true);
  };
  const getDefaultSensorStatus = () => {
    if (!allAllSensorstatuses || allAllSensorstatuses.length === 0) return {};
    let s = allAllSensorstatuses.find((s) => s.Name == 'Inactive');
    return { value: s.Id, label: s.Name };
  };
  const hasProperties = (obj) => {
    return Object.keys(obj).length > 0;
  };
  const isDisabled = (props) => {
    let isModelIdEmpty = isValueEmpty(props.values?.modelid?.value) || props.values?.modelid?.value == 0;
    let isStatusEmpty = isValueEmpty(props.values?.sensorstatusid?.value) || props.values?.sensorstatusid?.value == 0;
    return isModelIdEmpty || isStatusEmpty;
  };
  return (
    <>
      {loading ? (
        <Container>
          <AwpSpinner />
        </Container>
      ) : (
        <>
          <Formik
            initialValues={crudService.mapInitialValues({ entity: sensorData, getDefaultSensorStatus })}
            validateOnMount={true}
            validationSchema={Yup.object({
              serialnumber: Yup.string().max(50, 'Maximum 50 characters are allowed'),
              firmwareversion: Yup.string().max(25, 'Maximum 25 characters are allowed'),
              decommissiondate: Yup.date()
                .nullable()
                .test('decomltinstall', function () {
                  const { installationdate, decommissiondate } = this.parent;

                  if (installationdate && decommissiondate) {
                    if (installationdate > decommissiondate) {
                      return this.createError({
                        message: 'Decommission Date cannot be earlier than Installation Date.',
                        path: 'decommissiondate',
                      });
                    }
                  }
                  return true;
                }),
              metaConditions: Yup.string().test('metatest', 'found errors', (value, context) => {
                if (isJsonValueEmpty(fieldErrors)) return true;
                return isJsonValueEmpty(fieldErrors) || !hasProperties(fieldErrors);
              }),
            })}
            onSubmit={handleSubmit}
          >
            {(props) => (
              <Form>
                <span className="awp-text-btn" onClick={() => setMode('list')}>
                  List Sensors
                </span>
                <span className="mx-3">&gt;</span>
                <span>{title}</span>
                <Card border="light" className="page-card">
                  <Card.Header>
                    <Row className="mt-4"></Row>
                    {crudService.crud.getHeader()}
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      {mode !== FORM_MODE.CREATE && (
                        <Col sm={4} className="mb-4">
                          <FormTextReadOnly label="ID" textValue={sensorData.Id} />
                        </Col>
                      )}
                      <Col sm={4} className="mb-4">
                        <FormSelect
                          readOnly={
                            mode !== FORM_MODE.CREATE && !isValueEmpty(sensorData.StructuredAttributes)
                              ? sensorData.StructuredAttributes
                              : readOnly
                          }
                          label="Model (required)"
                          id="modelid"
                          name="modelid"
                          textValue={
                            sensorData.ModelNumber ? (
                              <Link to={Views.SensorModel} state={{ id: sensorData.ModelId }}>
                                {sensorData.ModelNumber}
                              </Link>
                            ) : (
                              ''
                            )
                          }
                          options={convertDropdownOptions(allAllSensormodels)}
                          placeholder={'Select ...'}
                          isMulti={false}
                        />
                      </Col>
                      <Col sm={4} className="mb-4">
                        <FormInput
                          label="Serial number"
                          instruction="(50 Characters max)"
                          readOnly={readOnly || sensorData.IsProtected === 'Y'}
                          name="serialnumber"
                          id="serialnumber"
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col sm={6} lg={4} className="mb-4">
                        <FormInput
                          label="Firmware version"
                          instruction="(25 Characters max)"
                          readOnly={readOnly || sensorData.IsProtected === 'Y'}
                          name="firmwareversion"
                          id="firmwareversion"
                        />
                      </Col>
                      <Col sm={6} lg={4} className="mb-4">
                        <FormSelect
                          readOnly={readOnly}
                          label="Status (required)"
                          id="sensorstatusid"
                          name="sensorstatusid"
                          textValue={sensorData.SensorStatusName ?? ''}
                          options={convertDropdownOptions(allAllSensorstatuses)}
                          placeholder={'Select ...'}
                          isMulti={false}
                        />
                      </Col>
                      <Col sm={6} lg={2} className="mb-4">
                        <FormDatePicker
                          readOnly={readOnly}
                          label="Installation date"
                          id="installationdate"
                          name="installationdate"
                          textValue={sensorData.InstallationDate}
                          showTime={{ isEnabled: false }}
                          dateFormat={['yyyy-MM-dd', 'yyyyMMdd']}
                        />
                      </Col>
                      <Col sm={6} lg={2} className="mb-4 pl-0 pr-pl-mini-15">
                        <FormDatePicker
                          readOnly={readOnly}
                          label="Decommission date"
                          id="decommissiondate"
                          name="decommissiondate"
                          textValue={sensorData.DecommissionDate}
                          showTime={{ isEnabled: false }}
                          dateFormat={['yyyy-MM-dd', 'yyyyMMdd']}
                        />
                      </Col>
                    </Row>
                    <Row>
                      {mode !== FORM_MODE.CREATE && (
                        <Col sm={4} className="mb-4">
                          <FormTextReadOnly
                            label="Weather station"
                            textValue={
                              isValueEmpty(sensorData.WeatherStation) ? (
                                ' - '
                              ) : (
                                <Link
                                  to={Views.WeatherStation}
                                  state={{ id: sensorData.WeatherStation.Id, mode: FORM_MODE.VIEW }}
                                >
                                  {sensorData.WeatherStation.Number + ' - ' + sensorData.WeatherStation.Name}
                                </Link>
                              )
                            }
                          />
                        </Col>
                      )}
                    </Row>
                    <>
                      {!readOnly && sensorData && (
                        <ModelMetaBoxEdit
                          key={'pmbox_edit_key_' + sensorData.Id + ''}
                          sensor={sensorData}
                          setSensorData={setSensorData}
                          fieldErrors={fieldErrors}
                          setFieldErrors={setFieldErrors}
                          props={props}
                        />
                      )}
                      {readOnly && sensorData && (
                        <ModelMetaBoxInfo key={'pmbox_info_key_' + sensorData.Id + ''} sensor={sensorData} />
                      )}
                    </>
                  </Card.Body>
                  <Card.Footer>
                    {!showDeleteConfirm ? (
                      <Row className="my-2">
                        <AwpFormActionButtons
                          title=" "
                          entityName={entityName}
                          buttonLabel={buttonLabel}
                          mode={mode}
                          setMode={setMode}
                          onClickDelete={onClickDelete}
                          permissions={[Permissions.WriteSensorEquipment]}
                          readOnly={readOnly}
                          disableSave={isDisabled(props)}
                          isAlignLeft={true}
                          btnCancelName={'Close'}
                          props={props}
                          isPristine={isEqual(crudService.cleanFormValues({ entity: props.values }), initialDto)}
                        />
                      </Row>
                    ) : (
                      <Row className="my-2">
                        <AwpYesNoDialog
                          action={deleteSensor}
                          setShowYesNoConfirm={setShowDeleteConfirm}
                          props={props}
                        />
                      </Row>
                    )}
                  </Card.Footer>
                </Card>
              </Form>
            )}
          </Formik>
          <AwpValidationErrorModal setErrors={setEntityErrors} errors={entityErrors} />
        </>
      )}
    </>
  );
};

export default SensorEdit;
